import React from 'react'

// Table components
import CustomTableHeader from "../../global/Table/CustomTableHeader";
import { Checkbox, CircularProgress, IconButton, TablePagination } from "@material-ui/core";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import GlobalTableHead from "../../global/Table/GlobalTableHead";
import TableBody from "@material-ui/core/TableBody";
import SearchableTable from "../../global/Search/SearchableTable";

import FormControlLabel from "@material-ui/core/FormControlLabel";

// Material UI
import TableCell from "@material-ui/core/TableCell";
import EditIcon from "@material-ui/icons/Edit";
import TableRow from "@material-ui/core/TableRow";
import DeleteIcon from '@material-ui/icons/Delete';
import SpeakerNotesIcon from '@material-ui/icons/SpeakerNotes';

// Constants
import { ASC, DESC } from "../../global/constants";
import {
    ROWS_PER_PAGE_LABEL,
    INVOICE_HEAD_CELLS,
    DEFAULT_SORT_BY_HEAD_CELL,
    SMART_SEARCH_DELAY,
    MIN_SMART_SEARCH_LENGTH,
    SMART_SEARCH_LOADING_MESSAGE
} from "./constants";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import FilterIcon from "../../shipments/FilterIcon";
import InvoiceFiltersDialog from '../InvoiceFiltersDialog';
import FilterTagsBar from '../../shipments/FilterTagsBar';
import { Slide } from "@material-ui/core";
import {AttachFile, CreditCard} from "@material-ui/icons";
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import { SHOW_CAD, SHOW_USD } from '../../accounting/transactions/constants';

class InvoicesTable extends SearchableTable {
    constructor(props) {
        super(props);
        this.state = {
            isSearch: false,
            searchValue: "",
            order: DESC,
            orderBy: DEFAULT_SORT_BY_HEAD_CELL,
            shownIndex: -1,
            deleteId: "",
            selectedStatus: ["Needs Review", "Approved for Payment", "Paid", "Duplicate"],
            fromDate: this.getFromDate(),
            toDate: this.getToDate(),
            filtersMap: {},
            showFilters: false,
            filtersMapBeforeOpenDialog: {},
            deletedTagsWithCookies: [],
            selectedCurrency: null,
        }
        this.onSearchValueChange = this.onSearchValueChange.bind(this)
        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.handleRequestSort = this.handleRequestSort.bind(this)
        this.getDisplayed = this.getDisplayed.bind(this)
        this.handleDelete = this.handleDelete.bind(this)
        this.selectAll = this.selectAll.bind(this)
    }

    formatDollar = (amount) => { // Regex expression taken from https://stackoverflow.com/questions/2901102
        return '$'+amount.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
    }

    async componentDidMount() {
        await this.setState({
            searchValue: this.props.searchText,
            filtersMap: this.props.tableSetting?.supplierInvoiceFilterSetting,
            selectedStatus: this.props.tableSetting?.supplierInvoiceFilterSetting?.selectedStatus ?? []
        })
        let fromDate = this.state.fromDate
        let toDate = this.state.toDate
        let storage = JSON.parse(localStorage.getItem("supplierInvoices"))
        const hasStoredDates = !!storage &&
            ((storage.fromDate !== null && storage.fromDate !== undefined) ||
                (storage.toDate !== null && storage.toDate !== undefined))
        if (hasStoredDates) {
            const expiry = parseInt(JSON.parse(localStorage.getItem("expiry")))
            if (expiry > new Date().getTime()) {
                this.getNewDates()
                if (storage.fromDate !== null && storage.fromDate !== undefined) {
                    fromDate = new Date(storage.fromDate)
                }
                if (storage.toDate !== null && storage.toDate !== undefined) {
                    toDate = new Date(storage.toDate)
                }
            } else {
                localStorage.removeItem("supplierInvoices")
                fromDate = this.getFromDate()
                toDate = this.getToDate()
                this.setState({
                    fromDate: fromDate,
                    toDate: toDate,
                    filtersMap: {}
                })
            }
        }
        // the list always comes back from the server so what is shown matches the collection,
        // not just the invoices that happen to already be in the store
        this.fetchSupplierInvoices(fromDate, toDate, true)
        // a search that was still active when the table unmounted (opening an invoice, for one) keeps
        // its rows, so re-check whether the reloaded list can answer it on its own
        if (this.props.searchText) {
            this.scheduleSmartSearch()
        }
    }

    componentWillUnmount() {
        clearTimeout(this.smartSearchTimer)
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.searchText !== this.props.searchText || this.state.searchValue !== this.props.searchText) {
            this.initializeSearchText()
        }
        // while the dialog is open the user owns the filters map - a save that lands mid edit must not
        // overwrite the PO number / reference id they are still typing
        if (prevProps.tableSetting?.supplierInvoiceFilterSetting !== this.props.tableSetting?.supplierInvoiceFilterSetting
            && !this.state.showFilters) {
            // the saved filters carry supplier / PO number / reference id, all of which are resolved
            // server side, so the list is pulled again once they are restored
            this.setState({
                filtersMap: this.addFilterDates(this.props.tableSetting?.supplierInvoiceFilterSetting),
                selectedStatus: this.props.tableSetting?.supplierInvoiceFilterSetting?.selectedStatus ?? []
            }, () => this.fetchSupplierInvoices(this.state.fromDate, this.state.toDate))
        }
    }

    initializeSearchText = () => {
        this.setState({ searchValue: this.props.searchText })
    }

    onSearchValueChange(e) {
        this.setState({
            isSearch: e.target.value.length > 0,
            searchValue: e.target.value
        }, this.scheduleSmartSearch);
        this.props.setPage(0)
        this.props.handleSearchChange(e)
    };

    /**
     * The rows the table works off: the invoices loaded for the selected date range, or - when the
     * search bar had to fall back to a PO number lookup - whatever that lookup brought back.
     */
    getTableData = () => {
        const smartSearchRows = this.props.smartSearchRows ?? []
        return smartSearchRows.length > 0 ? smartSearchRows : (this.props.data ?? [])
    }

    /**
     * Rows to render, search and filters applied. Lookup results are narrowed on PO number, the same
     * way the server matched them, rather than through the generic text search - that searches every
     * field and would drop the invoice over nothing more than a stray space in what was typed.
     */
    getFilteredRows = () => {
        const smartSearchRows = this.props.smartSearchRows ?? []
        const rows = smartSearchRows.length > 0
            ? this.filterByPoNumber(smartSearchRows)
            : this.filterBySearch(this.props.data ?? [])
        return this.applyOverviewFilters(rows)
    }

    filterByPoNumber = (rows) => {
        const searchValue = this.state.searchValue.trim().toLowerCase()
        if (!searchValue) {
            return rows
        }
        return rows.filter(row => String(row?.poNumber ?? "").toLowerCase().includes(searchValue))
    }

    // typing is not a request per keystroke, the lookup waits until the user pauses
    scheduleSmartSearch = () => {
        clearTimeout(this.smartSearchTimer)
        // a couple of characters match too much of the collection to be worth looking up
        if (this.state.searchValue.trim().length < MIN_SMART_SEARCH_LENGTH) {
            this.clearSmartSearch()
            return
        }
        this.smartSearchTimer = setTimeout(this.runSmartSearch, SMART_SEARCH_DELAY)
    }

    /**
     * The loaded invoices are searched first. Only when none of them match does the search go to the
     * server, which returns every invoice whose PO number contains it, whatever the date range.
     */
    runSmartSearch = () => {
        const searchValue = this.state.searchValue.trim()
        if (this.filterBySearch(this.props.data ?? []).length > 0) {
            this.clearSmartSearch()
            return
        }
        // narrowing the text can only narrow the last lookup's rows, so it needs no new request
        if (this.filterByPoNumber(this.props.smartSearchRows ?? []).length > 0) {
            return
        }
        this.props.fetchSupplierInvoiceByPoNumber(searchValue)
    }

    clearSmartSearch = () => {
        if (this.props.smartSearchRows?.length > 0) {
            this.props.setSmartSearchRows([])
        }
    }

    getFromDate = () => {
        var date = new Date();
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setDate(date.getDate() - 14)
        return date;
    }

    getToDate = () => {
        var date = new Date();
        date.setHours(23);
        date.setMinutes(59);
        date.setSeconds(59);
        return date;
    }

    getNewDates = () => {
        let filter = {...this.state.filtersMap}
        let storage = JSON.parse(localStorage?.getItem("supplierInvoices"))
        if (storage.fromDate !== null && storage.fromDate !== undefined ) {
            const temp = new Date(storage.fromDate)
            filter.fromDate = temp;
            this.setState({
                fromDate: temp
            })
        }
        if (storage.toDate !== null && storage.toDate !== undefined) {
            const temp = new Date(storage.toDate)
            filter.toDate = temp;
            this.setState({
                toDate: temp
            })
        }

        if (filter) {
            this.setState({
                filtersMap: filter,
            })
        }
    }

    isDefaultFromDate = (someDate) => {
        const defaultFromDate = this.getFromDate()
        return someDate.getDate() == defaultFromDate.getDate() &&
            someDate.getMonth() == defaultFromDate.getMonth() &&
            someDate.getFullYear() == defaultFromDate.getFullYear()
    }


    isToday = (someDate) => {
        const today = new Date()
        return someDate.getDate() == today.getDate() &&
            someDate.getMonth() == today.getMonth() &&
            someDate.getFullYear() == today.getFullYear()
    }

    addFilterDates = (filters) => {
        if (filters) {
            if (!this.isDefaultFromDate(this.state.fromDate)) {
                filters['fromDate'] = this.state.fromDate
            }
            if (!this.isToday(this.state.toDate)) {
                filters['toDate'] = this.state.toDate
            }
        }
        return filters
    }

    handleChangePage = (event, newPage) => {
        this.props.setPage(newPage)
    };
    handleChangeRowsPerPage = (event) => {
        this.props.setRowsPerPage(event.target.value)
        this.props.setPage(0)
    };
    handleRequestSort = (event, property) => {
        const isAsc = this.state.orderBy === property && this.state.order === ASC;
        this.setState({
            order: isAsc ? DESC : ASC,
            orderBy: property,
        });
    };
    getDisplayed = (rows) => {
        if (!rows)
            return [];
        return [...rows.slice(this.props.page * this.props.rowsPerPage, (this.props.page + 1) * this.props.rowsPerPage)];
    }

    openDropdown(index) {
        let val = index
        if (this.state.shownIndex === index) val = -1
        this.setState({ shownIndex: val })
    }
    handleEdit(template) {
        this.props.setEditing(template)
    }
    handleDelete() {
        this.props.deleteInvoice({ supplierInvoiceId: this.state.deleteId }, () => window.location.reload())
        this.setState({ deleteId: "" })
    }
    handleCheck(e, invoice) {
        let newEntries = { ...this.props.checkedEntries, [invoice.supplierInvoiceId]: e.target.checked }
        this.props.setSelected(newEntries, !!this.numSelected(newEntries))
    }
    selectAll(e) {
        let newChecked = {}
        let allEntries = this.getDisplayed(this.stableSort(this.getFilteredRows(), this.getComparator(this.state.order, this.state.orderBy)))
        allEntries.map((entry) => newChecked[entry.supplierInvoiceId] = e.target.checked)
        this.props.setSelected(newChecked, !!this.numSelected(newChecked))
    }
    numSelected(checkedEntries) {
        let rows = this.getDisplayed(this.stableSort(this.getFilteredRows(), this.getComparator(this.state.order, this.state.orderBy)))
        return rows.reduceRight((accum, curr) => accum + (checkedEntries[curr.supplierInvoiceId] ?? 0), 0)
    }

    toggleFiltersDialog = () => {
        this.setState({
            showFilters: !this.state.showFilters,
            filtersMapBeforeOpenDialog: { ...this.state.filtersMap }
        })
    }

    handleStatusChange = (event) => {
        let filters = { ...this.state.filtersMap };
        let checked = event.target.checked
        let name = event.target.name
        let statuses = []
        if (checked) {
            statuses = [...this.state.selectedStatus, name]
            this.setState({
                selectedStatus: statuses
            });
        }
        // remove from the list
        else {
            let index = this.state.selectedStatus.indexOf(name)
            let selected = this.state.selectedStatus
            selected.splice(index, 1)
            statuses = selected
            this.setState({
                selectedStatus: selected ?? []
            });
        }
        filters.selectedStatus = statuses
        this.saveUpdatedStatus(filters);
        //this.props.setSelected(checked, true)
        // this.onFiltersInputChange(event);
    }

    onFiltersInputChange = (name, val) => {
        const filtersMap = { ...this.state.filtersMap };

        // if filter already exists, then update
        if (val !== "" && val.length !== 0) {
            filtersMap[name] = val;
        }
        else {
            delete filtersMap[name];
        }

        this.saveUpdatedStatus(filtersMap)

        this.setState({
            filtersMap: filtersMap,
        });

    }

    /**
     * PO number / reference id are free text, so they are only kept in state while the user types.
     * They are persisted and sent to the server when the dialog is applied.
     */
    onTextFilterChange = (name, value) => {
        const filtersMap = { ...this.state.filtersMap };

        if (value && value.trim() !== "") {
            filtersMap[name] = value;
        } else {
            delete filtersMap[name];
        }

        this.setState({
            filtersMap: filtersMap,
        });
    }

    saveUpdatedStatus = (filters) => {
        // copied first: the dates belong to the tag bar / local storage, not to the saved setting,
        // and stripping them off the caller's map would drop the date tags from the filter bar
        const savedFilters = { ...(filters ?? {}) }
        delete savedFilters['fromDate']
        delete savedFilters['toDate']
        this.props.saveTableSetting({
            userId: this.props.user.userId,
            tableSetting: null,
            changeFilters: false,
            changeSupplierInvoiceFilters: true,
            supplierInvoiceFilterSetting: savedFilters
        })
    }

    handleDateChange = (date, key) => {
        const filtersMap = { ...this.state.filtersMap };
        if (key === 'fromDate' && !this.isDefaultFromDate(date) ||
            key === 'toDate' && !this.isToday(date)) {
            filtersMap[key] = date;
        } else {
            delete filtersMap[key]
        }
        this.setState({
            filtersMap: filtersMap,
            [key]: date
        })
        this.props.handleDateChange(date, key)
    }

    onFiltersClose = (applyFilters, saveFilterSetting) => {
        // NOTE: changes are already applied as the user makes these changes,
        // but if the changes are cancelled, then the map is reverted to its
        // previous state
        this.setState({
            showFilters: false,
        })
        let filters = {}
        if (!applyFilters) {
            filters = { ...this.state.filtersMapBeforeOpenDialog }
            this.resetDateFilters(filters?.fromDate ?? this.getFromDate(), filters?.toDate ?? this.getToDate(), filters)
        } else {
            if (this.state.deletedTagsWithCookies.length > 0) {
                let storage = JSON.parse(localStorage.getItem("supplierInvoices"))
                this.state.deletedTagsWithCookies.forEach((tagName) => {
                    delete storage[tagName]
                })
                localStorage.setItem("supplierInvoices", JSON.stringify(storage))
                if(JSON.stringify(storage) == JSON.stringify({})) localStorage.removeItem("supplierInvoices")
                this.setState({deletedTagsWithCookies: []})
            }
            filters = { ...this.state.filtersMap }
            const expiry = new Date().setHours(23, 59, 59, 0);
            if (filters.fromDate !== null && filters.fromDate !== undefined) {
                let existing = localStorage.getItem("supplierInvoices")
                existing = existing ? JSON.parse(existing) : {};
                existing.fromDate = filters.fromDate;
                localStorage.setItem("supplierInvoices", JSON.stringify(existing));
                localStorage.setItem("expiry", JSON.stringify(expiry))
            }
            if (filters.toDate !== null && filters.toDate !== undefined) {
                let existing = localStorage.getItem("supplierInvoices")
                existing = existing ? JSON.parse(existing) : {};
                existing.toDate = filters.toDate;
                localStorage.setItem("supplierInvoices", JSON.stringify(existing));
                localStorage.setItem("expiry", JSON.stringify(expiry))
            }
            // only a date or supplier change needs a new list, a PO number / reference id is matched
            // against the invoices already loaded
            this.fetchSupplierInvoices(this.state.fromDate, this.state.toDate)
            this.props.setFetchedList(false)
        }
        if (saveFilterSetting) {
            this.saveUpdatedStatus(filters)
        }
    }

    resetDateFilters = (fromDate, toDate, filters) => {
        if (!this.isDefaultFromDate(fromDate)) {
            filters['fromDate'] = fromDate
        } else {
            delete filters['fromDate']
            fromDate = this.getFromDate()
        }
        if (!this.isToday(toDate)) {
            filters['toDate'] = toDate
        } else {
            delete filters['toDate']
            toDate = this.getToDate()
        }
        this.setState({
            fromDate: fromDate,
            toDate: toDate,
            filtersMap: filters,
            deletedTagsWithCookies: [],
        })
        this.props.handleDateChange(fromDate, 'fromDate')
        this.props.handleDateChange(toDate, "toDate")
    }

    onTagClickedWhenDialogueOpen = (event, tag) => {
        const filtersMap = { ...this.state.filtersMap };
        let tagValue = filtersMap[tag]
        let deletedTagsWithCookies = this.state.deletedTagsWithCookies
        delete filtersMap[tag];
        if (tag === 'fromDate') {
            let storage = JSON.parse(localStorage.getItem('supplierInvoices'))
            if (storage) {
                if (storage.fromDate !== null && storage.fromDate !== undefined) {
                    if (new Date(storage.fromDate).getTime() === new Date(tagValue).getTime()) {
                        this.setState({deletedTagsWithCookies: [...deletedTagsWithCookies, "fromDate"]})
                    }
                }
            }
            this.setState({
                fromDate: this.getFromDate()
            })
        }
        if (tag === 'toDate') {
            let storage = JSON.parse(localStorage.getItem('supplierInvoices'))
            if (storage) {
                if (storage.toDate !== null && storage.toDate !== undefined) {
                    if (new Date(storage.toDate).getTime() === new Date(tagValue).getTime()) {
                        this.setState({deletedTagsWithCookies: [...deletedTagsWithCookies, "toDate"]})
                    }
                }
            }
            this.setState({
                toDate: this.getToDate()
            })
        }
        this.saveUpdatedStatus(filtersMap)
        this.setState({
            filtersMap: filtersMap,
        });
    }

    onTagClicked = (event, tag) => {

        // remove the key from the filters map
        // clone the map, delete the key, set the state
        const filtersMap = { ...this.state.filtersMap };
        delete filtersMap[tag];
        let fromDate = this.state.fromDate
        let toDate = this.state.toDate
        if (tag === 'fromDate' || tag === 'toDate') {
            let storage = JSON.parse(localStorage.getItem('supplierInvoices'))
            if (storage) {
                delete storage[tag]
                localStorage.setItem("supplierInvoices", JSON.stringify(storage))
                if (JSON.stringify(storage) == JSON.stringify({})) localStorage.removeItem("supplierInvoices")
            }
            if (tag === 'fromDate') {
                fromDate = this.getFromDate()
            } else {
                toDate = this.getToDate()
            }
        }

        this.saveUpdatedStatus(filtersMap)
        // dropping a date or supplier tag widens what the server returns, dropping a PO number /
        // reference id tag only widens what is shown and is skipped by the request check
        this.setState({
            filtersMap: filtersMap,
            fromDate: fromDate,
            toDate: toDate,
        }, () => this.fetchSupplierInvoices(this.state.fromDate, this.state.toDate));
    }

    /**
     * Only the criteria the server resolves - PO number and reference id are matched against the
     * invoices this returns, so changing them never costs another request.
     */
    buildInvoiceQuery = (fromDate, toDate) => {
        const filters = this.state.filtersMap ?? {}
        return {
            fromDate: fromDate,
            toDate: toDate,
            suppliers: filters.suppliers ?? []
        }
    }

    fetchSupplierInvoices = (fromDate, toDate, force = false) => {
        const query = this.buildInvoiceQuery(fromDate, toDate)
        const queryKey = JSON.stringify(query)
        // filter restores can fire more than once for the same criteria, only the user asking for
        // them again (Apply, removing a tag) forces a repeat request
        if (!force && queryKey === this.lastQueryKey) {
            return
        }
        this.lastQueryKey = queryKey
        this.props.fetchSupplierInvoices(query)
    }

    applyOverviewFilters = (rows) => {
        if (this.state.selectedStatus) {
            rows = rows.filter((item) => this.state.selectedStatus.includes(item.status))
        }
        // PO number / reference id narrow the invoices already loaded for the date range. The value
        // has to match the whole field: "AX-251232" finds "AX-251232" and never "AX 251232".
        rows = this.filterByExactValue(rows, 'poNumber')
        rows = this.filterByExactValue(rows, 'referenceId')

        return rows;
    }

    filterByExactValue = (rows, field) => {
        const value = String(this.state.filtersMap?.[field] ?? "").trim()
        if (!value) {
            return rows
        }
        return rows.filter(row => String(row?.[field] ?? "").trim().toLowerCase() === value.toLowerCase())
    }

    removeStatusFilters = () => {
        let filters = { ...this.state.filtersMap }
        delete filters['selectedStatus']
        return filters
    }

    handleCurrencyChange = (currency) => {
        const newCurrency = this.state.selectedCurrency === currency ? null : currency
        this.setState({ selectedCurrency: newCurrency })
    }

    calculateCurrencyTotals = (invoice) => {
        if (!invoice) return null
        const invoiceSubTotal = parseFloat(invoice.invoiceSubTotal || 0)
        const invoiceTax = parseFloat(invoice.invoiceTax || 0)
        const invoiceTotal = parseFloat(invoice.invoiceTotal || 0)
        return {
            invoiceSubTotal,
            invoiceTax,
            invoiceTotal
        }
    }

      applyCurrencyFilter = (invoices) => {
        if (!this.state.selectedCurrency) return invoices

        const targetCurrency = this.state.selectedCurrency.toUpperCase()

        return invoices.filter(invoice => {
            if (invoice.currency && invoice.currency.toUpperCase() === targetCurrency) {
                return true
            }
            if (invoice.supplierInvoiceItems && Array.isArray(invoice.supplierInvoiceItems)) {
                return invoice.supplierInvoiceItems.some(item =>
                    item.currencyCode?.toUpperCase() === targetCurrency
                )
            }
            return false
        })
    }

    render() {
         const allData = this.stableSort(
            this.getFilteredRows(),
            this.getComparator(this.state.order, this.state.orderBy)
        );

        // Paginated slice of allData
        const rows = this.getDisplayed(allData);

        // Currency filter applied to paginated rows — used for rendering table rows
        const filteredInvoices = this.applyCurrencyFilter(rows)

        // Currency filter applied to ALL data (not paginated) — used for computing totals
        const allFilteredInvoices = this.applyCurrencyFilter(allData)

        // Determine if we are on the last page
        const totalFilteredCount = allFilteredInvoices.length
        const isLastPage = totalFilteredCount > 0 &&
            (this.props.page + 1) * this.props.rowsPerPage >= totalFilteredCount

        // Compute totals from the full filtered dataset (all pages)
        const totalRow = allFilteredInvoices.reduce(
            (acc, invoice) => {
                const totals = this.calculateCurrencyTotals(invoice)
                if (totals) {
                    acc.invoiceSubTotal += totals.invoiceSubTotal
                    acc.invoiceTax += totals.invoiceTax
                    acc.invoiceTotal += totals.invoiceTotal
                }
                return acc
            },
            { invoiceSubTotal: 0, invoiceTax: 0, invoiceTotal: 0 }
        )

        const formattedTotalRow = {
            invoiceSubTotal: `$${totalRow.invoiceSubTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            invoiceTax: `$${totalRow.invoiceTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            invoiceTotal: `$${totalRow.invoiceTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        }


        return (
          <>
            <div className="filter-tag-wrapper">
              <FilterTagsBar
                filtersMap={this.removeStatusFilters()}
                onClick={
                  this.state.showFilters
                    ? this.onTagClickedWhenDialogueOpen
                    : this.onTagClicked
                }
              />
            </div>
            <CustomTableHeader
              searchText={this.props.searchText}
              onSearchValueChange={this.onSearchValueChange}
              pagination={
                <TablePagination
                  className="table-pagination"
                  labelRowsPerPage={ROWS_PER_PAGE_LABEL}
                  rowsPerPageOptions={[
                    5,
                    25,
                    50,
                    100,
                    { value: this.getTableData().length, label: 'All' },
                  ]}
                  count={
                    !this.getTableData()
                      ? 0
                      : this.applyCurrencyFilter(this.getFilteredRows()).length
                  }
                  rowsPerPage={this.props.rowsPerPage}
                  page={this.props.page}
                  onChangePage={this.handleChangePage}
                  onChangeRowsPerPage={this.handleChangeRowsPerPage}
                />
              }
              showFilters={this.state.showFilters}
              filtersComponent={
                <Slide
                  in={this.state.showFilters}
                  mountOnEnter
                  unmountOnExit
                  timeout={300}
                >
                  <InvoiceFiltersDialog
                    open={this.state.showFilters}
                    onClose={this.onFiltersClose}
                    onChange={this.onFiltersInputChange}
                    onTextFilterChange={this.onTextFilterChange}
                    showTextFilters={true}
                    listItems={this.props.suppliers}
                    handleDateChange={this.handleDateChange}
                    filtersMap={this.state.filtersMap}
                    clearFilters={this.clearFiltersMap}
                    onTagClicked={this.onTagClicked}
                    fromDate={this.state.fromDate}
                    toDate={this.state.toDate}
                    fieldName={'suppliers'}
                    fieldTitle={'Supplier'}
                  />
                </Slide>
              }
              customFilterField={
                <>
                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            this.state.selectedStatus
                              ? this.state.selectedStatus.includes(
                                  'Needs Review'
                                )
                              : false
                          }
                          onChange={this.handleStatusChange}
                          name="Needs Review"
                          style={{ color: '#acd685' }}
                        />
                      }
                      label="Needs Review"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            this.state.selectedStatus
                              ? this.state.selectedStatus.includes(
                                  'Approved for Payment'
                                )
                              : false
                          }
                          onChange={this.handleStatusChange}
                          name="Approved for Payment"
                          style={{ color: '#acd685' }}
                        />
                      }
                      label="Approved for Payment"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            this.state.selectedStatus
                              ? this.state.selectedStatus.includes('Paid')
                              : false
                          }
                          onChange={this.handleStatusChange}
                          name="Paid"
                          style={{ color: '#acd685' }}
                        />
                      }
                      label="Paid"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            this.state.selectedStatus
                              ? this.state.selectedStatus.includes('Duplicate')
                              : false
                          }
                          onChange={this.handleStatusChange}
                          name="Duplicate"
                          style={{ color: '#acd685' }}
                        />
                      }
                      label="Duplicate"
                    />
                    <span
                      style={{
                        fontSize: '25px',
                        paddingRight: '18px',
                      }}
                    >
                      |
                    </span>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={this.state.selectedCurrency === 'CAD'}
                          onChange={() =>
                            this.handleCurrencyChange('CAD')
                          }
                          icon={<CircleOutlinedIcon />}
                          checkedIcon={<CircleIcon />}
                        />
                      }
                      label={SHOW_CAD}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={this.state.selectedCurrency === 'USD'}
                          onChange={() =>
                            this.handleCurrencyChange('USD')
                          }
                          icon={<CircleOutlinedIcon />}
                          checkedIcon={<CircleIcon />}
                        />
                      }
                      label={SHOW_USD}
                    />
                  </div>
                </>
              }
              filterIcon={
                <Tooltip title={'Filter'} style={{ paddingTop: 10 }}>
                  <IconButton
                    className="filterButton"
                    size="small"
                    aria-label={'Filter'}
                    onClick={this.toggleFiltersDialog}
                  >
                    <FilterIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            {this.props.smartSearchLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' }}>
                <CircularProgress size={60} />
                <p>{SMART_SEARCH_LOADING_MESSAGE}</p>
              </div>
            ) : (
            <TableContainer>
              <Table size="medium">
                <GlobalTableHead
                  isCreatePurchaseOrderTable={false}
                  isShipmentTable={false}
                  isPurchaseOrderTable={false}
                  headCells={INVOICE_HEAD_CELLS}
                  onSelectAllClick={this.selectAll}
                  numSelected={this.numSelected(this.props.checkedEntries)}
                  isInvoiceTable={true}
                  order={this.state.order}
                  orderBy={this.state.orderBy}
                  onRequestSort={this.handleRequestSort}
                  rowCount={rows.length}
                />

                <TableBody>
                  {filteredInvoices.map((invoice, index) => (
                    <TableRow
                      hover
                      onDoubleClick={() => this.handleEdit(invoice)}
                      key={'invoice-' + index}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => this.handleEdit(invoice)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell style={{ padding: '0.2rem' }}>
                        <Checkbox
                          checked={
                            this.props.checkedEntries[
                              invoice.supplierInvoiceId
                            ] ?? false
                          }
                          onChange={(e) => this.handleCheck(e, invoice)}
                        />
                      </TableCell>
                      {INVOICE_HEAD_CELLS.slice(
                        0,
                        INVOICE_HEAD_CELLS.length - 1
                      ).map((headCell, index) => (
                        <TableCell
                          style={{
                            textAlign: headCell.rightAlign ? 'right' : 'left',
                            padding: '5px',
                            paddingRight: headCell.rightAlign ? '50px' : '5px',
                            color:
                              headCell.id === 'status' &&
                              (invoice.status === 'Needs Review' ||
                                invoice.status === 'Duplicate')
                                ? 'red'
                                : 'black',
                          }}
                          key={index}
                        >
                          {headCell.addDollar
                            ? this.formatDollar(invoice[headCell.id])
                            : invoice[headCell.id]}
                        </TableCell>
                      ))}
                      {invoice.referenceId &&
                      invoice.paymentDate &&
                      invoice?.bankAccount?.accountNumber ? (
                        <TableCell>
                          <IconButton>
                            <Tooltip
                              title={
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <div>{invoice.referenceId}</div>
                                  <div>{invoice.paymentDate}</div>
                                  <div>
                                    {invoice.bankAccount.name}{' '}
                                    {invoice.bankAccount.accountNumber}
                                  </div>
                                </div>
                              }
                            >
                              <CreditCard variant="outlined" fontSize="small" />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                      ) : (
                        <TableCell style={{ padding: '35px' }}></TableCell>
                      )}
                      {invoice.notes !== '' && invoice.notes !== null ? (
                        <TableCell>
                          <IconButton>
                            <Tooltip title={invoice.notes}>
                              <SpeakerNotesIcon />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                      ) : (
                        <TableCell style={{ padding: '35px' }}></TableCell>
                      )}
                      {invoice.files?.length && invoice.files?.length > 0 ? (
                        <TableCell>
                          <IconButton>
                            <AttachFile />
                          </IconButton>
                        </TableCell>
                      ) : (
                        <TableCell style={{ padding: '35px' }}></TableCell>
                      )}
                      <TableCell style={{ padding: '5px', width: '205px' }}>
                        {this.state.deleteId !== invoice.supplierInvoiceId ? (
                          <IconButton
                            onClick={() =>
                              this.setState({
                                deleteId: invoice.supplierInvoiceId,
                              })
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        ) : (
                          <div>
                            <Button
                              onClick={() => this.setState({ deleteId: '' })}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={this.handleDelete}
                              variant="contained"
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                       ))}

                     {this.state.selectedCurrency && isLastPage && (
                      <TableRow style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>

                       <TableCell style={{ padding: '0.2rem' }} />

                        <TableCell colSpan={INVOICE_HEAD_CELLS.length - 4}></TableCell>
                        <TableCell style={{ textAlign: 'right', paddingRight: '50px' }}>
                               { formattedTotalRow.invoiceSubTotal}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right', paddingRight: '50px' }}>
                          { formattedTotalRow.invoiceTax}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right', paddingRight: '50px' }}>
                            {formattedTotalRow.invoiceTotal}
                        </TableCell>

                        <TableCell style={{ textAlign: 'right', paddingRight: '50px' }}/>
                          <TableCell style={{ textAlign: 'right', paddingRight: '50px' }}/>
                            <TableCell style={{ textAlign: 'right', paddingRight: '50px' }}/>
                              <TableCell style={{ textAlign: 'right', paddingRight: '50px' }}/>
                                <TableCell style={{ textAlign: 'right', paddingRight: '50px' }}/>

                      </TableRow>)
                    }

                </TableBody>
              </Table>
            </TableContainer>
            )}
          </>
        );
    }
}

export default InvoicesTable